import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { Field, Select, TextInput, useToast } from '../ui';

/** True when the server refused because support access has not been opened. */
export const needsSupportAccess = (error: unknown): boolean =>
  error instanceof ApiError && error.code === 'SUPPORT_ACCESS_REQUIRED';

/**
 * Shown in place of a hotel's details when a support user has no open
 * grant: why, which ticket, for how long. The hotel sees all three, and every
 * page opened under it, in their own Privacy & Compliance page.
 */
export function SupportAccessRequest({
  tenantId,
  propertyId,
  onOpened,
}: {
  tenantId: string;
  propertyId?: string;
  onOpened: () => void;
}) {
  const { push, fail } = useToast();
  const [reason, setReason] = useState('');
  const [ticketReference, setTicket] = useState('');
  const [minutes, setMinutes] = useState('60');

  const open = useMutation({
    mutationFn: () =>
      api.post('/api/platform/privacy/support-access', {
        tenantId,
        ...(propertyId ? { propertyId } : {}),
        reason,
        ticketReference,
        minutes: Number(minutes),
      }),
    onSuccess: () => {
      push('Support access open', 'ok');
      onOpened();
    },
    onError: fail,
  });

  return (
    <div className="col gap-14">
      <p className="small">
        Opening this account's details needs a reason and a ticket. The hotel can see that you opened it, why,
        and what you looked at.
      </p>
      <Field label="Why" hint="At least a sentence - the hotel reads this">
        <textarea className="input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <div className="form-grid">
        <TextInput label="Ticket or reference" value={ticketReference} onChange={(e) => setTicket(e.target.value)} />
        <Select label="For" value={minutes} onChange={(e) => setMinutes(e.target.value)}>
          <option value="15">15 minutes</option>
          <option value="30">30 minutes</option>
          <option value="60">1 hour</option>
          <option value="120">2 hours</option>
          <option value="240">4 hours</option>
        </Select>
      </div>
      <div>
        <button
          className="btn primary"
          disabled={open.isPending || reason.trim().length < 10 || ticketReference.trim().length < 2}
          onClick={() => open.mutate()}
        >
          Open support access
        </button>
      </div>
    </div>
  );
}
